import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { DriverShell } from "@/components/shells";
import { Panel, Spinner, Btn, Field, Input, Textarea, Select } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { loadDriverContext, createBooking } from "@/lib/driverData";
import { useDraftPersistence, clearDraft } from "@/lib/useDraftPersistence";
import { HIRE_VEHICLE_TYPES, HIRE_CLASSES, HIRE_PROVISIONAL_NOTE } from "@/lib/vda";

const MODES = {
  hire_vehicle: {
    title: "Hire vehicle",
    intro: "Ask your Venson team for a temporary replacement vehicle.",
    reasonLabel: "Why do you need a hire vehicle?",
    reasonPlaceholder: "e.g. vehicle off the road for repair",
  },
  other: {
    title: "General enquiry",
    intro: "Ask your Venson team anything else about your vehicle.",
    reasonLabel: "How can we help?",
    reasonPlaceholder: "Tell us what you need",
  },
};

export default function DriverRequest() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const type = MODES[sp.get("type")] ? sp.get("type") : "other";
  const mode = MODES[type];
  const { data: ctx, loading } = useAsync(() => loadDriverContext(), []);
  const [form, setForm] = useState({ from_date: "", to_date: "", description: "", hire_type: "car", hire_class: "small" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const draftScope = ctx?.vehicle?.id ? `request_${ctx.vehicle.id}_${type}` : null;
  useDraftPersistence({ scope: draftScope, form, setForm, enabled: !!draftScope, onRestore: () => toast.info("Restored your previous progress") });

  if (loading) return <DriverShell back="/driver"><Spinner /></DriverShell>;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error("Please tell us a little more."); return; }
    setSaving(true);
    try {
      const details = { flow: type };
      if (type === "hire_vehicle") {
        const cls = (HIRE_CLASSES[form.hire_type] || []).find((c) => c.value === form.hire_class);
        details.from_date = form.from_date || null;
        details.to_date = form.to_date || null;
        details.vehicle_type = form.hire_type;
        details.vehicle_class = form.hire_class;
        details.vehicle_class_label = cls?.label;
        details.provisional_classes = true;
        details.hire_kind = ctx.customer?.hire_policy === "required" ? "required" : "requested";
      }
      const id = await createBooking(ctx, {
        request_type: type, request_category: type, request_details: details,
        description: form.description || null,
        preferred_date_1: type === "hire_vehicle" ? (form.from_date || null) : null,
      });
      clearDraft(draftScope);
      toast.success("Request submitted and routed to your Venson team.");
      nav(`/driver/booking/${id}`, { replace: true });
    } catch (err) {
      toast.error(err.message || "We couldn't submit your request. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <DriverShell back="/driver" title={mode.title}>
      <Panel className="p-3 mb-4 text-sm text-muted-foreground">{mode.intro}</Panel>
      <form onSubmit={submit} className="space-y-4" data-testid="request-form">
        {type === "hire_vehicle" && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Vehicle type">
                <Select data-testid="request-hire-type" value={form.hire_type} onChange={(e) => { set("hire_type", e.target.value); set("hire_class", (HIRE_CLASSES[e.target.value] || [])[0]?.value); }}>
                  {HIRE_VEHICLE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Select>
              </Field>
              <Field label="Class">
                <Select data-testid="request-hire-class" value={form.hire_class} onChange={(e) => set("hire_class", e.target.value)}>
                  {(HIRE_CLASSES[form.hire_type] || []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </Select>
              </Field>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">{HIRE_PROVISIONAL_NOTE}</p>
            <div className="grid grid-cols-2 gap-2">
              <Field label="From"><Input data-testid="request-from" type="date" value={form.from_date} onChange={(e) => set("from_date", e.target.value)} /></Field>
              <Field label="Until"><Input data-testid="request-to" type="date" value={form.to_date} onChange={(e) => set("to_date", e.target.value)} /></Field>
            </div>
          </>
        )}
        <Field label={mode.reasonLabel}>
          <Textarea data-testid="request-description" rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={mode.reasonPlaceholder} />
        </Field>
        <Btn type="submit" loading={saving} data-testid="request-submit" className="w-full">Submit request</Btn>
      </form>
    </DriverShell>
  );
}
